import { useCallback, useEffect, useMemo, useRef } from 'react'
import { CellEditingStoppedEvent, ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { setAlertQueue } from 'cache/alerts'
import { defaultColDef } from 'components/AgGrid'
import { useUpdateDecisionEditPageLines } from 'hooks/use-update-decision-edit-page-lines'
import { CurrencyConvertLineItem, KeyValueResponse } from 'queries/useCurrencyConvertEditPage'
import { MessageType } from 'ts/enums/messageType.enums'
import {
  ProductDetailsLineItem,
  ProductsDetailsData,
} from 'ts/interfaces/productsDetails.interface'
import { useRefreshDataPointsCell } from '../../hooks/useRefreshDataPointsCell'
import type { ColumnKey } from '../../utils/dynamicColorMap'
import { applyDynamicColumnColor, buildDynamicHeaderStyles } from '../../utils/dynamicColorMap'
import type { ApprovalOption, ComparisonTab, GuidanceType } from '../ComparisonTabs'
import {
  createDataPointsCellRenderer,
  RowDataPoints,
} from '../DataPoint/createDataPointsCellRenderer'
import { applyColumnVisibility } from '../shared/applyColumnVisibility'
import { renderEditableValue } from '../shared/EditableCell'
import {
  parsePercentageEditValue,
  stringToNumber,
  validateBdNetValue,
  ValidationHandle,
} from '../validations'
import { frameworkComponents, productsDetailsGridOptions } from './agGridConfig/agGridConfig'
import { getProductsDetailsColDefs } from './agGridConfig/columnDefs'
import styles from './styles.module.scss'

interface ProductsDetailsGridProps {
  data: ProductsDetailsData | null
  currencyData?: CurrencyConvertLineItem[]
  isUSDSelected: boolean
  activeTabs?: ComparisonTab[]
  selectedApprovalTypes?: string[]
  selectedGuidanceTypes?: GuidanceType[]
  colorMap?: Partial<Record<ColumnKey, string>>
  approvalOptions?: ApprovalOption[]
  dealId?: number
  onRefresh?: () => Promise<void>
  onEditSuccess?: () => void
}

const productsDetailsRowMapper = (row: ProductDetailsLineItem): RowDataPoints => ({
  baseline: row.dataPoints,
  ogmOrMargin: row.requestNetMarginPercentage,
  requestedValue: row.requestNetMarginPercentageDb ?? row.requestNetMarginPercentage,
  guidanceValues: {
    expertValue: row.calcGuidanceExpertPercentage,
    typicalValue: row.calcGuidanceTypicalPercentage,
    floorValue: row.calcGuidanceFloorPercentage,
    top25Value: row.historicalTop25DiscountPercentage,
    medianValue: row.historicalMedianDiscountPercentage,
    bottom25Value: row.historicalBottom25DiscountPercentage,
  },
  approvalValues: row.approvalData
    ? Object.fromEntries(
        Object.entries(row.approvalData).map(([id, detail]) => [id, detail.ogmPercent])
      )
    : {},
})

export const ProductsDetailsGrid = ({
  data,
  currencyData,
  isUSDSelected,
  activeTabs,
  selectedApprovalTypes,
  selectedGuidanceTypes,
  colorMap,
  approvalOptions,
  dealId,
  onRefresh,
  onEditSuccess,
}: ProductsDetailsGridProps) => {
  const gridRef = useRef<AgGridReact<ProductDetailsLineItem>>(null)
  const { updateDecisionEditPageLines } = useUpdateDecisionEditPageLines()

  const customDefaultColDef: ColDef = useMemo(
    () => ({
      ...defaultColDef,
      sortable: false,
      filter: false,
    }),
    []
  )

  const rowData = useMemo(() => {
    if (!data) return []

    if (isUSDSelected && currencyData) {
      return data.lineItems.map((item) => {
        let convertedBdNet = item.extendedRequestBdNet

        const matchingCurrencyItem = currencyData.find(
          (currItem: CurrencyConvertLineItem) =>
            currItem.lineNumber === item.id &&
            currItem.productNumber === item.productGenericNumberText
        )

        if (matchingCurrencyItem) {
          const bdNetConversion = matchingCurrencyItem.keyValueResponses.find(
            (kvr: KeyValueResponse) => kvr.key === 'requestBdNetPriceAmount1'
          )
          if (bdNetConversion) {
            convertedBdNet = bdNetConversion.amount
          }
        }

        return {
          ...item,
          extendedRequestBdNet: convertedBdNet,
          requestBdNetPriceAmountUpdated:
            item.requestBdNetPriceAmountUpdated != null ? convertedBdNet : null,
          authBdnetPriceAmountUpdated:
            item.authBdnetPriceAmountUpdated != null ? convertedBdNet : null,
        }
      })
    }

    return data.lineItems
  }, [data, currencyData, isUSDSelected])

  const pinnedBottomRowData = useMemo(() => {
    if (!data?.totals) return []

    if (isUSDSelected && currencyData) {
      const { totalConvertedQuantity, totalConvertedBDNetAmount } = currencyData?.[0] ?? {}
      return [
        {
          id: 'totals',
          lineTypeCode: 'Total',
          estimatedQuantity: totalConvertedQuantity,
          bdNet: totalConvertedBDNetAmount,
        },
      ]
    }

    return [
      {
        id: 'totals',
        lineTypeCode: 'Total',
        estimatedQuantity: data.totals.qty,
        bdNet: data.totals.bdNet,
      },
    ]
  }, [data, isUSDSelected, currencyData])

  const containerClassName = useMemo(
    () => clsx('ag-theme-alpine', styles.productsDetailsGridContainer),
    []
  )

  useEffect(() => {
    if (isUSDSelected) {
      gridRef.current?.api?.stopEditing(true)
    }
    gridRef.current?.api?.refreshCells({ force: true })
  }, [isUSDSelected])

  const refreshDataPointsCell = useRefreshDataPointsCell<ProductDetailsLineItem>()

  const handleCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent<ProductDetailsLineItem>) => {
      const editedValue = event.newValue ?? event.value

      if (!dealId || isUSDSelected || event.node?.rowPinned) {
        return
      }

      const field = event.colDef.field
      if (field !== 'extendedRequestBdNet' && field !== 'requestNetMarginPercentage') {
        return
      }

      const isRequestNetMarginField = field === 'requestNetMarginPercentage'

      if (editedValue === event.oldValue) {
        return
      }

      const lineNumber = Number(event.data?.id)
      const plCode = event.data?.assosiatePlCode
      const parsedPercentageValue =
        field === 'requestNetMarginPercentage' ? parsePercentageEditValue(editedValue) : null

      if (field === 'requestNetMarginPercentage' && parsedPercentageValue === null) {
        if (event.node && typeof event.node.setDataValue === 'function') {
          event.node.setDataValue(field, event.oldValue)
        }
        refreshDataPointsCell(event)
        return
      }

      const parsedValue =
        field === 'requestNetMarginPercentage' ? parsedPercentageValue : stringToNumber(editedValue)

      // Prevent invalid values that are coerced to 0 from being saved
      // Only accept intentional 0 values (entered as '0' or 0)
      if (
        editedValue !== null &&
        editedValue !== undefined &&
        editedValue !== '' &&
        parsedValue === 0
      ) {
        const actualValue = typeof editedValue === 'string' ? editedValue.trim() : editedValue
        if (actualValue !== '0' && actualValue !== 0) {
          if (event.node && typeof event.node.setDataValue === 'function') {
            event.node.setDataValue(field, event.oldValue)
          }
          if (isRequestNetMarginField) {
            refreshDataPointsCell(event)
          }
          return // Invalid value that was coerced to 0 - reject the update
        }
      }

      if (!lineNumber || Number.isNaN(parsedValue) || !plCode) {
        if (isRequestNetMarginField) {
          refreshDataPointsCell(event)
        }
        return
      }

      updateDecisionEditPageLines({
        variables: {
          updateRequest: {
            dealId,
            isPLTotalView: false,
            productUpdates: [
              {
                lineNumber,
                requestBdNetPriceAmount: field === 'extendedRequestBdNet' ? parsedValue : null,
                requestNetMarginPercentage:
                  field === 'requestNetMarginPercentage' ? parsedValue : null,
                authBdNetPriceAmount: null,
                authNetMarginPercentage: null,
                plCode,
              },
            ],
          },
        },
      })
        .then((result) => {
          const updateResult = result.data?.updateDecisionEditPageLines

          if (updateResult?.isSuccess) {
            if (isRequestNetMarginField) {
              refreshDataPointsCell(event)
            }
            onEditSuccess?.()

            if (onRefresh) {
              onRefresh().catch((error) => {
                console.error('Failed to refresh data after update:', error)
                setAlertQueue({
                  type: MessageType.WARNING,
                  message: 'Changes saved but failed to refresh. Please reload the page.',
                })
              })
            }
          } else {
            setAlertQueue({
              type: MessageType.ERROR,
              message: updateResult?.error ?? 'Failed to save changes. Please try again.',
            })
            if (event.node && field && typeof event.node.setDataValue === 'function') {
              event.node.setDataValue(field, event.oldValue)
            }
            if (isRequestNetMarginField) {
              refreshDataPointsCell(event)
            }
          }
        })
        .catch((error) => {
          console.error('Failed to update decision edit page lines:', error)
          setAlertQueue({
            type: MessageType.ERROR,
            message: 'Failed to save changes. Please try again.',
          })
          if (event.node && field && typeof event.node.setDataValue === 'function') {
            event.node.setDataValue(field, event.oldValue)
          }
          if (isRequestNetMarginField) {
            refreshDataPointsCell(event)
          }
        })
    },
    [
      dealId,
      isUSDSelected,
      onRefresh,
      updateDecisionEditPageLines,
      onEditSuccess,
      refreshDataPointsCell,
    ]
  )

  const columnDefs = useMemo<ColDef<ProductDetailsLineItem>[]>(() => {
    const showRequestedPrice = activeTabs?.includes('requestedPrice') ?? false
    const guidanceSelections = selectedGuidanceTypes ?? []
    const showFloorGuidance = guidanceSelections.includes('floor')
    const showTypicalGuidance = guidanceSelections.includes('typical')
    const showExpertGuidance = guidanceSelections.includes('expert')
    const showTop25Guidance = guidanceSelections.includes('top25')
    const showMedianGuidance = guidanceSelections.includes('median')
    const showBottom25Guidance = guidanceSelections.includes('bottom25')

    const approvalPrefixHideMap: Partial<Record<string, boolean>> = {}
    approvalOptions?.forEach(({ id }) => {
      approvalPrefixHideMap[`approval-${id}-`] = !(selectedApprovalTypes?.includes(id) ?? false)
    })

    return getProductsDetailsColDefs(showRequestedPrice, approvalOptions).map((col) => {
      const withPrefixVisibility = applyColumnVisibility(
        col,
        {},
        {
          'requested-': !showRequestedPrice,
          ...approvalPrefixHideMap,
        }
      )
      if (withPrefixVisibility) {
        return colorMap
          ? applyDynamicColumnColor(withPrefixVisibility, colorMap)
          : withPrefixVisibility
      }

      if (!col.colId && col.field === 'extendedRequestBdNet') {
        return {
          ...col,
          editable: (params) => !params.node?.rowPinned && !params.context?.isUSDSelected,
          cellEditor: 'agNumberCellEditor',
          valueParser: (params) => params.newValue,
          valueSetter: (params) => validateBdNetValue(params),
          cellRenderer: renderEditableValue,
          cellClass: (params) =>
            params.node?.rowPinned || params.context?.isUSDSelected ? '' : 'editable-bordered-cell',
        }
      }

      if (!col.colId && col.field === 'requestNetMarginPercentage') {
        return {
          ...col,
          editable: (params) => !params.node?.rowPinned && !params.context?.isUSDSelected,
          cellEditor: 'agNumberCellEditor',
          valueParser: (params) => Number(params.newValue),
          valueSetter: (params) => ValidationHandle(params),
          cellRenderer: renderEditableValue,
          cellClass: (params) =>
            params.node?.rowPinned || params.context?.isUSDSelected ? '' : 'editable-bordered-cell',
        }
      }

      if (col.field === 'dataPoints') {
        return {
          ...col,
          cellRenderer: createDataPointsCellRenderer(
            productsDetailsRowMapper,
            showRequestedPrice,
            selectedApprovalTypes,
            guidanceSelections,
            colorMap
          ),
        }
      }

      const withFieldVisibility =
        applyColumnVisibility(
          col,
          {
            historicalBottom25DiscountPercentage: !showBottom25Guidance,
            historicalMedianDiscountPercentage: !showMedianGuidance,
            historicalTop25DiscountPercentage: !showTop25Guidance,
            calcGuidanceFloorPercentage: !showFloorGuidance,
            calcGuidanceTypicalPercentage: !showTypicalGuidance,
            calcGuidanceExpertPercentage: !showExpertGuidance,
          },
          {}
        ) ?? col

      return colorMap ? applyDynamicColumnColor(withFieldVisibility, colorMap) : withFieldVisibility
    })
  }, [activeTabs, selectedApprovalTypes, selectedGuidanceTypes, colorMap, approvalOptions])

  return (
    <section className={containerClassName} aria-label="Products details table">
      {colorMap && Object.keys(colorMap).length > 0 && (
        // eslint-disable-next-line react/no-danger
        <style dangerouslySetInnerHTML={{ __html: buildDynamicHeaderStyles(colorMap) }} />
      )}
      <AgGridReact<ProductDetailsLineItem>
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={customDefaultColDef}
        pinnedBottomRowData={pinnedBottomRowData}
        context={{ isUSDSelected }}
        onCellEditingStopped={handleCellEditingStopped}
        components={frameworkComponents}
        {...productsDetailsGridOptions}
      />
    </section>
  )
}
